<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { useAuthModals } from "@/composables/useAuthModals";
import { usePaywallModal } from "@/composables/usePaywallModal";
import { FORMATS, F } from "../../settings/free-tools/formats-data.js";
import { capOf, isUnwritableTarget } from "../../settings/free-tools/format-capabilities.js";
import { useBatch } from "@/composables/useBatch.js";
import { pollJob, jobWaitMsg } from "@/composables/useJobPoll.js";
import { tierOf, perFileMax } from "@/settings/free-tools/batch-limits.js";

const props = defineProps({
  // 工具名，如 "PNG to DST" / "Image Converter"
  name: { type: String, default: "" },
  // 根转换器/压缩器模式（"converter" | "compressor" | ""）：源自动识别;converter 的目标由用户下拉选,
  // compressor 目标=源同格式。配对页保持 root=""（行为完全不变）。
  root: { type: String, default: "" },
});

// 向父组件上报工具阶段（drop=初始 / work=已上传待转换 / done=转换完成可下载），
// 父组件据此给左右+底部广告换 key 重新挂载（每次阶段切换重载一次广告）。
const emit = defineEmits(["stage-change"]);
const store = useStore();

/* ---------- format lookup tables (ported from the design prototype) ---------- */
// 目标格式 → [显示label, canvas/后端用的 fmt]
const TARGET_MAP = {
  png: ["PNG", "png"], jpg: ["JPG", "jpeg"], jpeg: ["JPG", "jpeg"], webp: ["WEBP", "webp"],
  ico: ["ICO", "ico"], svg: ["SVG", "svg"], gif: ["GIF", "gif"], avif: ["AVIF", "avif"],
  cmyk: ["CMYK", "cmyk"], dng: ["DNG", "dng"], cr2: ["CR2", "cr2"], cr3: ["CR3", "cr3"],
  arw: ["ARW", "arw"], nef: ["NEF", "nef"], raf: ["RAF", "raf"], raw: ["RAW", "raw"],
  pes: ["PES", "pes"], dst: ["DST", "dst"], exp: ["EXP", "exp"],
  jef: ["JEF", "jef"], vp3: ["VP3", "vp3"], xxx: ["XXX", "xxx"], hus: ["HUS", "hus"], emd: ["EMD", "emd"],
  cdr: ["CDR", "cdr"], pdf: ["PDF", "pdf"], // pdf 已在 BACKEND/OFFICE_SET 却漏了 TARGET_MAP → X-to-pdf 页 curFmt 为空、转换报 'X -> ''(office 后端本就支持 pdf 目标)。
  icns: ["ICNS", "icns"], plist: ["PLIST", "plist"], dwg: ["DWG", "dwg"], dxf: ["DXF", "dxf"],
  plt: ["PLT", "plt"], vsdx: ["VSDX", "vsdx"], heic: ["HEIC", "heic"], apng: ["APNG", "apng"],
  cur: ["CUR", "cur"], xcf: ["XCF", "xcf"], visio: ["VSDX", "vsdx"], mp4: ["MP4", "mp4"],
  mov: ["MOV", "mov"], webm: ["WEBM", "webm"], avi: ["AVI", "avi"], mkv: ["MKV", "mkv"],
  // 视频长尾(ffmpeg)
  "3gp": ["3GP", "3gp"], asf: ["ASF", "asf"], flv: ["FLV", "flv"], m2ts: ["M2TS", "m2ts"],
  m4v: ["M4V", "m4v"], mpeg: ["MPEG", "mpeg"], mpg: ["MPG", "mpg"], mts: ["MTS", "mts"],
  rm: ["RM", "rm"], vob: ["VOB", "vob"], wmv: ["WMV", "wmv"], swf: ["SWF", "swf"],
  mp3: ["MP3", "mp3"], wav: ["WAV", "wav"], ogg: ["OGG", "ogg"], m4a: ["M4A", "m4a"],
  // 音频长尾(ffmpeg)
  aac: ["AAC", "aac"], flac: ["FLAC", "flac"], aiff: ["AIFF", "aiff"], wma: ["WMA", "wma"],
  opus: ["Opus", "opus"], amr: ["AMR", "amr"], au: ["AU", "au"], ra: ["RA", "ra"], caf: ["CAF", "caf"],
  mid: ["MIDI", "mid"],
  grayscale: ["Grayscale", "grayscale"], rgb: ["RGB", "rgb"], // #6 前端近似色彩转换
};
// 源格式 → [显示label, <input accept>]
const SOURCE_MAP = {
  png: ["PNG", "image/png"], jpg: ["JPG", "image/jpeg"], jpeg: ["JPEG", "image/jpeg"],
  webp: ["WEBP", "image/webp"], heic: ["HEIC", ".heic,.heif"], heif: ["HEIF", ".heic,.heif"],
  jfif: ["JFIF", "image/jpeg,.jfif"], gif: ["GIF", "image/gif"], bmp: ["BMP", "image/bmp"],
  ico: ["ICO", "image/x-icon,.ico"], svg: ["SVG", "image/svg+xml,.svg"], avif: ["AVIF", "image/avif"],
  tiff: ["TIFF", "image/tiff,.tif,.tiff"], xmp: ["XMP", ".xmp"], cr3: ["CR3", ".cr3"],
  cr2: ["CR2", ".cr2"], arw: ["ARW", ".arw"], nef: ["NEF", ".nef"], raf: ["RAF", ".raf"],
  // 其余相机 RAW 源(dnglab 支持,<raw>-to-dng 配对页用):文件选择器 accept 显式带扩展名。
  crw: ["CRW", ".crw"], nrw: ["NRW", ".nrw"], sr2: ["SR2", ".sr2"], srf: ["SRF", ".srf"], orf: ["ORF", ".orf"], pef: ["PEF", ".pef"], srw: ["SRW", ".srw"], dcr: ["DCR", ".dcr"], kdc: ["KDC", ".kdc"], mrw: ["MRW", ".mrw"], "3fr": ["3FR", ".3fr"], mos: ["MOS", ".mos"], iiq: ["IIQ", ".iiq"], erf: ["ERF", ".erf"], mef: ["MEF", ".mef"], x3f: ["X3F", ".x3f"], rwl: ["RWL", ".rwl"],
  rw2: ["RW2", ".rw2"], dng: ["DNG", ".dng"], thm: ["THM", ".thm"], ithmb: ["ITHMB", ".ithmb"],
  raw: ["RAW", ".raw"], eps: ["EPS", ".eps,application/postscript"], ai: ["AI", ".ai"],
  cdr: ["CDR", ".cdr"], dxf: ["DXF", ".dxf"], dwg: ["DWG", ".dwg"], psd: ["PSD", ".psd"],
  pdf: ["PDF", "application/pdf,.pdf"], dst: ["DST", ".dst"], pes: ["PES", ".pes"],
  exp: ["EXP", ".exp"], icns: ["ICNS", ".icns"], plist: ["PLIST", ".plist"],
  jxr: ["JXR", ".jxr,.wdp"], p7s: ["P7S", ".p7s"], dds: ["DDS", ".dds"],
  video: ["Video", "video/*"], mp4: ["MP4", "video/mp4"], mov: ["MOV", "video/quicktime,.mov"],
  webm: ["WEBM", "video/webm"], avi: ["AVI", "video/x-msvideo,.avi"], mkv: ["MKV", "video/*,.mkv"],
  // 视频源长尾(mime 退而用 video/* + 扩展名)
  wmv: ["WMV", "video/*,.wmv"], flv: ["FLV", "video/*,.flv"], m4v: ["M4V", "video/mp4,.m4v"],
  mpeg: ["MPEG", "video/mpeg,.mpeg,.mpg"], mpg: ["MPG", "video/mpeg,.mpg"], "3gp": ["3GP", "video/*,.3gp"],
  mts: ["MTS", "video/*,.mts"], m2ts: ["M2TS", "video/*,.m2ts"], asf: ["ASF", "video/*,.asf"],
  vob: ["VOB", "video/*,.vob"], rm: ["RM", "video/*,.rm"],
  mp3: ["MP3", "audio/mpeg"], wav: ["WAV", "audio/wav"], audio: ["Audio", "audio/*"],
  ogg: ["OGG", "audio/ogg,.ogg"], m4a: ["M4A", "audio/mp4,.m4a"], aac: ["AAC", "audio/aac,.aac"],
  // 音频源长尾(mime 退而用 audio/* + 扩展名)
  flac: ["FLAC", "audio/flac,.flac"], wma: ["WMA", "audio/*,.wma"], aiff: ["AIFF", "audio/*,.aiff,.aif"],
  opus: ["Opus", "audio/*,.opus"], amr: ["AMR", "audio/*,.amr"], au: ["AU", "audio/*,.au"],
  ra: ["RA", "audio/*,.ra"], caf: ["CAF", "audio/*,.caf"],
  image: ["Image", "image/*"], rgb: ["RGB", "image/*"], grayscale: ["Grayscale", "image/*"],
  cmyk: ["CMYK", "image/jpeg,.jpg,.tif,.tiff"], // #6 cmyk 源（多为 CMYK JPEG，浏览器可解码）
};
// canvas 无法诚实产出的格式 → 走服务端（本切片后端只支持 pes|dst）。
// 注：cmyk 不再在此（#6 改为前端近似：to-cmyk 直出 CMYK TIFF，cmyk 源由浏览器解码）。
const BACKEND = {
  avif: 1, svg: 1, dng: 1, cr2: 1, cr3: 1, arw: 1, nef: 1, raf: 1, raw: 1,
  // 相机 RAW 源(dnglab→dng + rawpy 解码→图像):全部走后端。与 DNG_RAW_SRC 对齐,补齐此前缺失的。
  rw2: 1, crw: 1, nrw: 1, sr2: 1, srf: 1, orf: 1, pef: 1, srw: 1, dcr: 1, kdc: 1, mrw: 1, "3fr": 1, mos: 1, iiq: 1, erf: 1, mef: 1, x3f: 1, rwl: 1,
  pes: 1, dst: 1, exp: 1, jef: 1, vp3: 1, xxx: 1, hus: 1, emd: 1, cdr: 1, icns: 1, plist: 1, dxf: 1, plt: 1, hpgl: 1, vsdx: 1,
  xcf: 1, mp4: 1, mov: 1, webm: 1, avi: 1, mkv: 1, mp3: 1, wav: 1, ogg: 1, m4a: 1, aac: 1, flac: 1,
  // canvas 不能诚实编码/解码的栅格 → 走后端通用图像转换(Pillow/pillow-heif)。
  bmp: 1, tiff: 1, tif: 1, heic: 1, heif: 1,
  // 设计/办公格式 → 后端 office 容器(LibreOffice/ghostscript/imagemagick)。
  eps: 1, ai: 1, psd: 1, pdf: 1, wmf: 1, emf: 1, pub: 1, vsd: 1, svgz: 1, pdfa: 1, cur: 1, djvu: 1,
  // 选项3 已接:jxr(libjxr→IM)、dwg(ODA File Converter→LibreOffice)。栅格→svg 矢量化走 svg(已在上)。
  jxr: 1, dwg: 1,
  // 媒体长尾(ffmpeg → media 容器):视频 + 音频。
  flv: 1, mpg: 1, mpeg: 1, "3gp": 1, wmv: 1, m4v: 1, vob: 1, mts: 1, m2ts: 1, asf: 1, rm: 1,
  aiff: 1, wma: 1, opus: 1, amr: 1, au: 1, ra: 1, caf: 1,
  // 栅格长尾(core Pillow):canvas 解不了的冷门栅格,源/目标都走后端通用转换。
  tga: 1, pcx: 1, ppm: 1, pgm: 1, pbm: 1, pnm: 1, pam: 1, dib: 1, dds: 1, jps: 1,
  jp2: 1, jpc: 1, sgi: 1, sun: 1, ras: 1, xbm: 1, xpm: 1, im: 1,
  // 冷门历史栅格(office 容器 ImageMagick,/office/caps 实测可读;作源 → png/jpg)。
  miff: 1, vicar: 1, pct: 1, pict: 1, cals: 1, palm: 1, dcx: 1, fax: 1, g3: 1, g4: 1,
  xwd: 1, wbmp: 1, art: 1, avs: 1, otb: 1, dpx: 1, cin: 1, fits: 1, hdr: 1, jbig: 1,
  pcd: 1, psb: 1, wpg: 1, mat: 1, mac: 1, cut: 1, rla: 1, rle: 1, sct: 1, tim: 1,
  pix: 1, sfw: 1, dcm: 1, pwp: 1,
};
// 「暂不可做」判定不再硬编码 —— 统一读后端权威能力表 format-capabilities.js
// (源自 sharkfoto-fastapi/format-capabilities.csv)。规则:目标后端写不出(且非下方客户端近似)
// → Coming soon;源后端读不了 → Coming soon。后端新增可写目标后,重生成能力表即自动放开,
// 不必再两处维护(此前 COMING_SOON/CANT_WRITE_TGT 与能力表分叉:如 au/caf 表里可写却被硬禁、
// cur/flif/mng/ras/sun/viff/xpm 等死目标仍被列入下拉会 422)。详见 nuxt#26。
// #6:前端近似色彩转换的「目标」+ 浏览器可解码的「源」——这些组合走 client 不上后端,
// 故即便能力表把 cmyk 标为不可写,这里仍视为可做(to-cmyk 直出 CMYK TIFF)。
const CLIENT_FX = { cmyk: 1, grayscale: 1, rgb: 1 };
// 目标写不出(已知格式 & 能力表 writable=false),且不是客户端近似目标。
const cantWriteTarget = (f) => !!f && isUnwritableTarget(f) && !CLIENT_FX[f];
// 源读不了(已知格式 & 能力表 readable=false),且不是浏览器可解码的源。
const cantReadSource = (f) => { const c = capOf(f); return !!c && !c.readable && !DECODABLE[f]; };
const DECODABLE = { png: 1, jpg: 1, jpeg: 1, webp: 1, gif: 1, bmp: 1, svg: 1, ico: 1, jfif: 1, image: 1, cmyk: 1, grayscale: 1, rgb: 1 };
const ENC = { gif: "png" }; // canvas 不能直出 gif，先按 PNG 字节编码
// 每文件上限统一走 batch-limits.js 的 PER_FILE_MAX / perFileMax(单一来源,单文件+批量共用)。
const VIDEO_EXTS = /\.(mp4|mov|webm|avi|mkv|3gp|asf|flv|m2ts|m4v|mpeg|mpg|mts|rm|vob|wmv)$/i;
const AUDIO_EXTS = /\.(mp3|wav|ogg|m4a|aac|flac|aiff|aif|wma|opus|amr|au|ra|caf)$/i;

/* ---------- 根模式状态:源从上传文件后缀识别;converter 目标由用户下拉选 ---------- */
const detectedSrc = ref("");
// 根模式类目：按工具名推导（Video Converter→video / Audio Converter→audio / 否则 image）。
// 决定源占位符（未上传时显示哪类文件）+ 目标下拉列表（ROOT_TARGETS）+ pickedTgt 初值。
const rootCat = computed(() => {
  const n = (props.name || "").toLowerCase();
  if (n.includes("video")) return "video";
  if (n.includes("audio")) return "audio";
  return "image";
});
// pickedTgt 初值随类目（video→mp4 / audio→mp3 / image→png）。
const pickedTgt = ref(rootCat.value === "video" ? "mp4" : rootCat.value === "audio" ? "mp3" : "png");
// dng 目标:仅当源是 dnglab 支持的相机 RAW 时后端(dnglab)能原封装成真 DNG;成品图做不到。
// 与后端 app/dng_convert.py DNGLAB_RAW 对齐。ROOT_TARGETS(源相关)+ dngFromRaw(coming-soon 豁免)共用。
const DNG_RAW_SRC = {
  cr2: 1, cr3: 1, crw: 1, nef: 1, nrw: 1, arw: 1, sr2: 1, srf: 1, orf: 1, pef: 1,
  raf: 1, rw2: 1, dng: 1, srw: 1, dcr: 1, kdc: 1, mrw: 1, "3fr": 1, mos: 1, iiq: 1,
  erf: 1, mef: 1, x3f: 1, rwl: 1,
};
// 通用转换器目标 = 该类目下的全部格式。
// image：图像类（raster/vector/icon/animated/print）；video：type==='video'；audio：type==='audio'。
// 选中不支持的目标时由 isComingSoon 在 work 阶段拦截，不在此排除。随 rootCat 重算。
const ROOT_TARGETS = computed(() => {
  const cat = rootCat.value;
  const TYPES =
    cat === "video" ? ["video"] : cat === "audio" ? ["audio"] : ["raster", "vector", "icon", "animated", "print"];
  const PRI =
    cat === "video"
      ? ["mp4", "mov", "webm", "avi", "mkv", "gif"]
      : cat === "audio"
      ? ["mp3", "wav", "ogg", "m4a", "aac", "flac"]
      : ["png", "jpg", "jpeg", "webp", "avif", "gif", "bmp", "tiff", "ico", "pdf", "svg", "heic"];
  const seen = {}, out = [];
  FORMATS.forEach((r) => {
    const fmt = String(r[F.EXT]).toLowerCase();
    const ty = r[F.TYPE];
    if (!TYPES.includes(ty)) return;
    // 从源头杜绝死配对:后端写不出的目标(且非客户端近似 cmyk 等)不进下拉,选不到就不会 422。
    if (isUnwritableTarget(fmt) && !CLIENT_FX[fmt]) return;
    if (seen[fmt]) return;
    seen[fmt] = 1;
    out.push(fmt);
  });
  // dng 目标源相关:上传的源是 dnglab 支持的相机 RAW 才提供(否则 dng 被上面的不可写过滤挡掉)。
  // 未上传时 detectedSrc 为空 → 不提供;上传 cr2/nef/rw2… → 出现 dng,可 raw→dng。
  if (cat === "image" && DNG_RAW_SRC[detectedSrc.value] && !seen["dng"]) { out.push("dng"); seen["dng"] = 1; }
  // 常用格式排前面，其余字母序
  out.sort((a, b) => {
    const ia = PRI.indexOf(a), ib = PRI.indexOf(b);
    if (ia >= 0 && ib >= 0) return ia - ib;
    if (ia >= 0) return -1;
    if (ib >= 0) return 1;
    return a < b ? -1 : 1;
  });
  return out;
});

/* ---------- 从 name 推导 源/目标（"PNG to DST" → png / dst） ---------- */
const sourceKey = computed(() => {
  const m = (props.name || "").match(/^([a-z0-9]+)-to-/i);
  return m ? m[1].toLowerCase() : "";
});
const targetKey = computed(() => {
  const m = (props.name || "").match(/-to-([a-z0-9]+)$/i);
  return m ? m[1].toLowerCase() : "";
});
// name 里是 "PNG to DST"（带空格），slug 化后才是 "png-to-dst"。两种都吃。
const slug = computed(() =>
  (props.name || "").toLowerCase().trim().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
);
const src = computed(() => {
  if (props.root) return detectedSrc.value || rootCat.value;
  const m = slug.value.match(/^([a-z0-9]+)-to-/);
  return m ? m[1] : sourceKey.value;
});
const tgt = computed(() => {
  if (props.root === "compressor") return detectedSrc.value || ""; // 压缩=源同格式
  if (props.root === "converter") return pickedTgt.value; // 用户选的目标
  const m = slug.value.match(/-to-([a-z0-9]+)$/);
  return m ? m[1] : targetKey.value;
});

const sourceLabel = computed(() => (SOURCE_MAP[src.value] ? SOURCE_MAP[src.value][0] : "image"));
const sourceAccept = computed(() => (SOURCE_MAP[src.value] ? SOURCE_MAP[src.value][1] : "image/*"));
// 锁定的目标按钮 [label, fmt]
const targetEntry = computed(() => TARGET_MAP[tgt.value] || ["", ""]);
const targetLabel = computed(() => targetEntry.value[0]);
const curFmt = computed(() => targetEntry.value[1]);
// 目标是后端格式(pes/dst/exp…) 或 源是后端格式(cr2/nef/dng… 等需服务端解码) → 走后端。
const isBackend = computed(() => {
  if (CLIENT_FX[curFmt.value] && DECODABLE[src.value]) return false; // #6 色彩转换走前端近似
  // 统一原则(用户无感):源浏览器解不了(不在 DECODABLE)→ 自动走后端(Pillow/rawpy 能读的长尾:
  // heic/tiff/tga/dds/raw…),而不是在前端 canvas 硬试→"Could not read image"。真读不了的(能力表
  // readable=false)已被 isComingSoon 拦下,到不了这里。(AE 审计 ①)
  return !DECODABLE[src.value] || !!BACKEND[curFmt.value] || !!BACKEND[src.value];
});
// dng→dng 自配对无意义(dnglab 只是重封装),排除 → 落到 coming-soon,不当作真 converter。
// (DNG_RAW_SRC 定义在 ROOT_TARGETS 前,两处共用。)
const dngFromRaw = computed(
  () => (tgt.value === "dng" || curFmt.value === "dng") && src.value !== "dng" && !!DNG_RAW_SRC[src.value]
);
// "Coming soon":目标后端写不出(非客户端近似)或源后端读不了 → 工具禁用 + 提示。能力表驱动。
// 大产物(dng 39MB+)下载已走 worker 直连(绕开 Nitro 代理 CF 1102),故 raw→dng 恢复为可做。
const isComingSoon = computed(
  () =>
    dngFromRaw.value
      ? false
      : cantWriteTarget(tgt.value) || cantWriteTarget(curFmt.value) || cantReadSource(src.value)
);
const showQuality = computed(() => !isBackend.value && (curFmt.value === "jpeg" || curFmt.value === "webp"));
// 刺绣目标(全 8 种)才显示刺绣参数控件 + 走登录/付费拦截
// (hus/emd 用自补 hus_emd_writer;见 sharkfoto-fastapi libs/embroidery/hus_emd_writer)
const EMB_FORMATS = ["pes", "dst", "exp", "jef", "vp3", "xxx", "hus", "emd"];
const isEmbroidery = computed(() => isBackend.value && EMB_FORMATS.includes(curFmt.value));
// 目标格式类目：用 FORMATS 里 curFmt 对应行的 TYPE 列(video/audio)判定，决定显示媒体质量控件。
const tgtType = computed(() => {
  const f = (curFmt.value || "").toLowerCase();
  const row = FORMATS.find((r) => String(r[F.EXT]).toLowerCase() === f);
  return row ? row[F.TYPE] : "";
});
const isVideoTgt = computed(() => tgtType.value === "video");
const isAudioTgt = computed(() => tgtType.value === "audio");

// dropzone 文案（SSR 与 client 一致）
const dropTitle = computed(() => `Drop your ${sourceLabel.value} file here`);
const hint = computed(() => `or click to browse — ${sourceLabel.value} files`);
const btnText = computed(() => `Choose ${sourceLabel.value}`);

/* ---------- 响应式状态（client 才填充） ---------- */
const stage = ref("drop"); // 'drop' | 'work'
const dragging = ref(false);
const previewUrl = ref("");
// 预览类型:video→<video>、audio→<audio>、image→<img>、file→占位(RAW/CAD/PDF 等无法在浏览器解码的)
const previewKind = ref("image");
const thumbUrl = ref("");
const fileName = ref("");
const fileSize = ref("");
const busy = ref(false);
const resultShown = ref(false);
const resultFmt = ref("");
const resultSize = ref("");
const resultBlob = ref(null); // 客户端/同步产物 Blob（手动下载用；异步产物走 resultUrl，不取 blob）
const resultUrl = ref(""); // 异步产物:R2 公开域名直连下载 URL(对象自带 Content-Disposition,经 /download 页导航下载,任意大小、不经 Nitro 代理)
const resultDlName = ref(""); // 异步产物下载文件名(填 /download 卡片标题;真实文件名由对象元数据决定)
const resultExt = ref(""); // 产物扩展名
// R2 公开域名:异步结果对象直连下载用(与主站 AI 产物同源,对象自带 Content-Disposition)。
const userR2Base = (useRuntimeConfig().public.userR2Url || "").replace(/\/+$/, "");
const errorMsg = ref("");
const quality = ref(92);
const baseName = ref("image");

/* ---------- 刺绣转换参数（pes/dst/exp 目标用，对应后端 query 参数） ---------- */
const embQuality = ref("standard"); // draft | standard | fine | ultra
const widthMm = ref(100); // embroidery_width_mm，>0
const colorCount = ref(8); // embroidery_color_count，1-256
const rowSpacing = ref(0.8); // embroidery_row_spacing (mm) ← 引擎 spacing_mm
const stitchLength = ref(3.5); // embroidery_stitch_length (mm) ← 引擎 stitch_mm
const minArea = ref(5.0); // embroidery_min_area (mm²) ← 引擎 min_area_mm2
const EMB_QUALITIES = ["draft", "standard", "fine", "ultra"];

/* ---------- 视频/音频转换参数（媒体目标用，对应后端可选 query 参数） ---------- */
const videoResolution = ref(""); // "" 原分辨率 | "1080" | "720" | "480" | "360"（目标高度像素）
const videoQuality = ref("high"); // 'high' | 'medium' | 'low' → onConvert 时换算成 crf 20/25/30
const audioBitrate = ref(""); // "" 缺省 | "320k" | "256k" | "192k" | "128k" | "96k"
const audioSampleRate = ref(0); // 0 缺省 | 48000 | 44100 | 22050
const VIDEO_CRF_MAP = { high: 20, medium: 25, low: 30 };

/* ---------- 批量模式(nuxt#21):多文件 → 队列。复用现有格式检测 + canvas/后端两条路径 ---------- */
const RAW_SET = { cr2: 1, cr3: 1, nef: 1, arw: 1, raf: 1, rw2: 1, dng: 1, raw: 1, orf: 1, pef: 1, srw: 1, x3f: 1, thm: 1, ithmb: 1 };
const OFFICE_SET = { cdr: 1, vsdx: 1, vsd: 1, eps: 1, ai: 1, psd: 1, pdf: 1, wmf: 1, emf: 1, pub: 1, xcf: 1, jxr: 1, dwg: 1, visio: 1 };
const batchMode = ref(false);
const tierB = computed(() => tierOf(!!store.state.user.token, store.state.user.userInfo));
const tokenB = computed(() => store.state.user.token || "");
const userIdB = computed(() => (store.state.user.userInfo && store.state.user.userInfo.user_info_id) || "");
const batchCategory = computed(() => {
  if (isEmbroidery.value) return "embroidery";
  if (isVideoTgt.value) return "video";
  if (isAudioTgt.value) return "audio";
  if (RAW_SET[src.value]) return "raw";
  if (OFFICE_SET[curFmt.value] || OFFICE_SET[src.value]) return "office";
  return "image";
});
const batchClient = computed(() => !isBackend.value);
const _auth = useAuthModals();
const _paywall = usePaywallModal();
const batch = useBatch({
  slug,
  category: batchCategory,
  client: batchClient,
  tier: tierB,
  token: tokenB,
  userId: userIdB,
  clientConvert: (file) => convertFileCanvas(file),
  buildQuery: () => buildBatchQuery(),
  onSignup: () => _auth.openSignInModal(),
  onUpgrade: () => _paywall.openPaywall(["DST", "PES", "EXP", "JEF", "VP3", "HUS", "XXX", "EMD"]),
});

// 单文件 canvas 转换的「按文件」版本(批量客户端路径用),镜像 convertCanvas 但输入任意 file。
async function convertFileCanvas(file) {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => rej(new Error("Could not read image"));
      im.src = url;
    });
    const fmt = curFmt.value;
    const c = document.createElement("canvas");
    c.width = image.naturalWidth; c.height = image.naturalHeight;
    const ctx = c.getContext("2d");
    if (fmt === "jpeg" || fmt === "cmyk" || fmt === "grayscale" || fmt === "rgb") { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height); }
    ctx.drawImage(image, 0, 0);
    let blob, ext;
    if (fmt === "grayscale") {
      const id = ctx.getImageData(0, 0, c.width, c.height); const d = id.data;
      for (let i = 0; i < d.length; i += 4) { const l = Math.round(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]); d[i] = d[i + 1] = d[i + 2] = l; }
      ctx.putImageData(id, 0, 0);
      blob = await new Promise((r) => c.toBlob(r, "image/png")); ext = "png";
    } else if (fmt === "cmyk") { blob = await canvasToCmykTiff(c); ext = "tif"; }
    else if (fmt === "rgb") { blob = await new Promise((r) => c.toBlob(r, "image/png")); ext = "png"; }
    else if (fmt === "ico") { blob = await pngToIco(c); ext = "ico"; }
    else { const q = fmt === "png" ? undefined : quality.value / 100; blob = await new Promise((r) => c.toBlob(r, "image/" + (ENC[fmt] || fmt), q)); ext = fmt === "jpeg" ? "jpg" : fmt; }
    if (!blob) throw new Error("Conversion failed");
    return { blob, ext };
  } finally { URL.revokeObjectURL(url); }
}

// 批量后端 query(镜像 onConvert 的 qs 构造,所有文件共用一套参数)。
function buildBatchQuery() {
  let qs =
    "to_format=" + encodeURIComponent(curFmt.value) +
    "&from_format=" + encodeURIComponent(src.value) +
    "&embroidery_width_mm=" + encodeURIComponent(widthMm.value) +
    "&embroidery_color_count=" + encodeURIComponent(colorCount.value) +
    "&embroidery_row_spacing=" + encodeURIComponent(rowSpacing.value) +
    "&embroidery_stitch_length=" + encodeURIComponent(stitchLength.value) +
    "&embroidery_min_area=" + encodeURIComponent(minArea.value) +
    "&quality=" + encodeURIComponent(embQuality.value);
  if (isVideoTgt.value) {
    if (videoResolution.value) qs += "&video_resolution=" + encodeURIComponent(videoResolution.value);
    const crf = VIDEO_CRF_MAP[videoQuality.value];
    if (crf != null) qs += "&video_crf=" + encodeURIComponent(crf);
  } else if (isAudioTgt.value) {
    if (audioBitrate.value) qs += "&audio_bitrate=" + encodeURIComponent(audioBitrate.value);
    if (audioSampleRate.value > 0) qs += "&audio_sample_rate=" + encodeURIComponent(audioSampleRate.value);
  }
  return qs;
}

function enterBatch(files) {
  batchMode.value = true;
  batch.clearAll();
  batch.addFiles(files);
  emit("stage-change", "work");
}
function addMoreFiles() { if (fileInput.value) fileInput.value.click(); }
// 套餐 / 目标变化 → 重算锁定;队列清空 → 退出批量回到 dropzone。
watch([tierB, batchCategory, batchClient], () => batch.reevaluate());
watch(() => batch.items.value.length, (n) => {
  if (batchMode.value && n === 0) { batchMode.value = false; emit("stage-change", "drop"); }
});

// 分辨率不允许放大:只列出 < 源视频高度的档位(源高度从 <video> loadedmetadata 读)
const srcVideoHeight = ref(0);
const RES_LADDER = [1080, 720, 480, 360];
const resolutionOptions = computed(() => {
  const h = srcVideoHeight.value;
  const out = [{ v: "", t: "Original" }];
  RES_LADDER.forEach((r) => { if (!h || r < h) out.push({ v: String(r), t: r + "p" }); });
  return out;
});
function onVideoMeta(e) {
  srcVideoHeight.value = (e && e.target && e.target.videoHeight) || 0;
  // 已选档位 >= 源高度(=放大或等于原始)→ 回退 Original
  if (videoResolution.value && Number(videoResolution.value) >= srcVideoHeight.value) {
    videoResolution.value = "";
  }
}

/* ---------- 长任务进度二级提示（纯前端，后端无进度回传） ---------- */
const slowProcessingMsg = ref("");

// 切换 Quality 档位时，Width/Colors/Spacing/Stitch/MinArea 联动到该档推荐默认值（取自引擎 PRESETS；用户仍可手动改）。
const QUALITY_DEFAULTS = {
  draft: { width: 80, colors: 4, spacing: 1.2, stitch: 4.0, minArea: 12.0 },
  standard: { width: 100, colors: 8, spacing: 0.8, stitch: 3.5, minArea: 5.0 },
  fine: { width: 120, colors: 12, spacing: 0.6, stitch: 3.0, minArea: 3.0 },
  ultra: { width: 150, colors: 16, spacing: 0.45, stitch: 2.5, minArea: 1.0 },
};
watch(embQuality, (q) => {
  const d = QUALITY_DEFAULTS[q];
  if (d) {
    widthMm.value = d.width;
    colorCount.value = d.colors;
    rowSpacing.value = d.spacing;
    stitchLength.value = d.stitch;
    minArea.value = d.minArea;
  }
});

// 广告重载信号：仅在「上传(drop→work)」「转换完成(reloadGen 自增)」「重置(→drop)」时变化。
// 改参数只动 resultShown，不动 stage/reloadGen → adReloadKey 不变 → 改参数不重载广告。
const reloadGen = ref(0);
const adReloadKey = computed(() => stage.value + (reloadGen.value ? ":" + reloadGen.value : ""));
watch(adReloadKey, (s) => emit("stage-change", s));

// 转换完成后改动任一转换参数 → 作废旧结果，主按钮回到 Convert（避免下载到旧参数的文件）。
function invalidateResult() {
  resultShown.value = false;
  resultBlob.value = null;
  resultUrl.value = "";
  resultExt.value = "";
  if (lastDownloadUrl) { URL.revokeObjectURL(lastDownloadUrl); lastDownloadUrl = null; }
}
watch([embQuality, widthMm, colorCount, rowSpacing, stitchLength, minArea, quality, pickedTgt,
       videoResolution, videoQuality, audioBitrate, audioSampleRate], () => {
  if (resultShown.value) invalidateResult();
  // 批量模式同理:改了「Convert all to」/质量等参数 → 作废已完成结果并重新入队,
  // allDone 转 false,主按钮从「Download all」复位成「Convert all」(否则停在下载旧格式的死状态)。
  if (batchMode.value && !batch.busy.value && (batch.doneCount.value > 0 || batch.failedCount.value > 0)) {
    batch.resetResults();
  }
});

const fileInput = ref(null);
let rawFile = null; // 原始 File（不进响应式，避免无谓代理）
let img = null; // client-only Image 对象
let lastDownloadUrl = null; // 仅保留最近一个下载用 blob: URL，换新前先释放，避免内存泄漏

/* ---------- helpers ---------- */
function fmtBytes(b) {
  if (b < 1024) return b + " B";
  if (b < 1048576) return (b / 1024).toFixed(1) + " KB";
  return (b / 1048576).toFixed(2) + " MB";
}
function revokeUrls() {
  if (!import.meta.client) return;
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  if (thumbUrl.value && thumbUrl.value !== previewUrl.value) URL.revokeObjectURL(thumbUrl.value);
  previewUrl.value = "";
  thumbUrl.value = "";
}

function loadFile(file) {
  if (!import.meta.client || !file) return;
  errorMsg.value = "";
  // 每文件上限按类目统一取自 batch-limits.js 的 PER_FILE_MAX(单一来源,与批量一致)。
  // 视频/音频偏保守(媒体容器只有 2 实例 + 转码慢,大文件必超时;见 AE 审计 ③ / nuxt#47)。
  const limitBytes = perFileMax(batchCategory.value);
  if (file.size > limitBytes) {
    const limitMB = Math.round(limitBytes / (1024 * 1024));
    const fileMB = (file.size / (1024 * 1024)).toFixed(1);
    errorMsg.value = `File is too large (${fileMB} MB). Max is ${limitMB} MB for this tool.`;
    return;
  }
  const isImg = file.type.startsWith("image/");
  // 接受范围 = 任意后端能处理的格式（Object.keys(BACKEND) 拼成正则，确保新增格式自动被接受）
  // + 配对页/前端能读的常见源（thm/ithmb/xmp/p7s 等不在 BACKEND 但仍是合法上传）。
  const BACKEND_EXTS = new RegExp("\\.(" + Object.keys(BACKEND).join("|") + ")$", "i");
  const EXTRA_EXTS = /\.(thm|ithmb|xmp|p7s|cmyk)$/i;
  const isMedia =
    file.type.startsWith("video/") ||
    file.type.startsWith("audio/") ||
    BACKEND_EXTS.test(file.name) ||
    EXTRA_EXTS.test(file.name);
  if (!isImg && !isMedia) {
    errorMsg.value = "Please choose a valid file for this tool.";
    return;
  }
  // root 转换器按类目校验上传类型（配对页 props.root 为空、或 image 类目保持原逻辑不变）。
  if (props.root && rootCat.value === "video") {
    if (!(file.type.startsWith("video/") || VIDEO_EXTS.test(file.name))) {
      errorMsg.value = "Please upload a video file.";
      return;
    }
  } else if (props.root && rootCat.value === "audio") {
    if (!(file.type.startsWith("audio/") || AUDIO_EXTS.test(file.name))) {
      errorMsg.value = "Please upload an audio file.";
      return;
    }
  }

  revokeUrls();
  rawFile = file;
  baseName.value = file.name.replace(/\.[^.]+$/, "") || "image";
  if (props.root) {
    const ext = (file.name.match(/\.([a-z0-9]+)$/i) || [, ""])[1].toLowerCase();
    detectedSrc.value = ext || "image";
  }
  fileName.value = file.name;
  fileSize.value = fmtBytes(file.size) + " · original";
  invalidateResult();

  const url = URL.createObjectURL(file);
  previewUrl.value = url;
  thumbUrl.value = url;
  srcVideoHeight.value = 0; // 新文件:清源高度,等 <video> loadedmetadata 重新探测
  const mt = file.type || "";
  previewKind.value = mt.startsWith("video/")
    ? "video"
    : mt.startsWith("audio/")
      ? "audio"
      : mt.startsWith("image/")
        ? "image"
        : "file";

  if (isImg) {
    img = new Image();
    img.onload = () => {
      stage.value = "work";
    };
    img.onerror = () => {
      // 无法解码（如 HEIC/RAW）— 仍进入工作区,改用占位预览(否则 <img> 显示破图)
      previewKind.value = "file";
      stage.value = "work";
    };
    img.src = url;
  } else {
    img = null;
    stage.value = "work";
  }
}

/* ---------- dropzone 交互 ---------- */
function onPickClick() {
  if (fileInput.value) fileInput.value.click();
}
function onBrowse(e) {
  e.stopPropagation();
  if (fileInput.value) fileInput.value.click();
}
function handleFiles(files) {
  if (!files || !files.length) return;
  if (batchMode.value) { batch.addFiles(files); return; } // 已在批量 → add more
  if (files.length > 1) { enterBatch(files); return; }     // 多文件 → 进批量
  loadFile(files[0]);                                       // 单文件 → 原有单文件流
}
function onInputChange(e) {
  handleFiles(e.target.files);
  e.target.value = ""; // 允许 add more / 重选同名文件
}
function onDragEnter(e) {
  e.preventDefault();
  dragging.value = true;
}
function onDragOver(e) {
  e.preventDefault();
  dragging.value = true;
}
function onDragLeave(e) {
  e.preventDefault();
  dragging.value = false;
}
function onDrop(e) {
  e.preventDefault();
  dragging.value = false;
  handleFiles(e.dataTransfer && e.dataTransfer.files);
}

/* ---------- 客户端 canvas 转换（png/jpg/webp/ico/gif 离线可用） ---------- */
async function pngToIco(canvas) {
  let c = canvas;
  if (c.width > 256 || c.height > 256) {
    const s = Math.min(256 / c.width, 256 / c.height);
    const t = document.createElement("canvas");
    t.width = Math.max(1, Math.round(c.width * s));
    t.height = Math.max(1, Math.round(c.height * s));
    t.getContext("2d").drawImage(c, 0, 0, t.width, t.height);
    c = t;
  }
  const pngBlob = await new Promise((r) => c.toBlob(r, "image/png"));
  const png = new Uint8Array(await pngBlob.arrayBuffer());
  const header = new Uint8Array(22);
  const dv = new DataView(header.buffer);
  dv.setUint16(0, 0, true);
  dv.setUint16(2, 1, true);
  dv.setUint16(4, 1, true); // reserved, type=icon, count=1
  header[6] = c.width >= 256 ? 0 : c.width;
  header[7] = c.height >= 256 ? 0 : c.height;
  header[8] = 0;
  header[9] = 0;
  dv.setUint16(10, 1, true);
  dv.setUint16(12, 32, true);
  dv.setUint32(14, png.length, true);
  dv.setUint32(18, 22, true);
  return new Blob([header, png], { type: "image/x-icon" });
}

// #6：把 canvas 像素编码为真正的 CMYK TIFF（Photometric=5）。RGB→CMYK 用朴素公式（无 ICC，即“近似”）。
function canvasToCmykTiff(c) {
  const ctx = c.getContext("2d");
  const W = c.width, H = c.height;
  const rgba = ctx.getImageData(0, 0, W, H).data;
  const px = W * H;
  const cmyk = new Uint8Array(px * 4);
  for (let i = 0, j = 0; i < px; i++) {
    const r = rgba[i * 4] / 255, g = rgba[i * 4 + 1] / 255, b = rgba[i * 4 + 2] / 255;
    const k = 1 - Math.max(r, g, b);
    let cc = 0, m = 0, y = 0;
    if (k < 1) { cc = (1 - r - k) / (1 - k); m = (1 - g - k) / (1 - k); y = (1 - b - k) / (1 - k); }
    cmyk[j++] = Math.round(cc * 255);
    cmyk[j++] = Math.round(m * 255);
    cmyk[j++] = Math.round(y * 255);
    cmyk[j++] = Math.round(k * 255);
  }
  const NENT = 11;
  const ifdSize = 2 + NENT * 12 + 4;
  const bpsOff = 8 + ifdSize;        // BitsPerSample 数组 [8,8,8,8]
  const dataOff = bpsOff + 8;        // 像素数据
  const buf = new ArrayBuffer(dataOff + px * 4);
  const dv = new DataView(buf);
  const u8 = new Uint8Array(buf);
  u8[0] = 0x49; u8[1] = 0x49; dv.setUint16(2, 42, true); dv.setUint32(4, 8, true); // "II", 42, IFD@8
  let o = 8;
  dv.setUint16(o, NENT, true); o += 2;
  const E = (tag, type, count, value) => { dv.setUint16(o, tag, true); dv.setUint16(o + 2, type, true); dv.setUint32(o + 4, count, true); dv.setUint32(o + 8, value, true); o += 12; };
  E(256, 4, 1, W);        // ImageWidth
  E(257, 4, 1, H);        // ImageLength
  E(258, 3, 4, bpsOff);   // BitsPerSample → 偏移
  E(259, 3, 1, 1);        // Compression = none
  E(262, 3, 1, 5);        // PhotometricInterpretation = Separated (CMYK)
  E(273, 4, 1, dataOff);  // StripOffsets
  E(277, 3, 1, 4);        // SamplesPerPixel
  E(278, 4, 1, H);        // RowsPerStrip
  E(279, 4, 1, px * 4);   // StripByteCounts
  E(284, 3, 1, 1);        // PlanarConfiguration = chunky
  E(332, 3, 1, 1);        // InkSet = CMYK
  dv.setUint32(o, 0, true); // next IFD = 0
  dv.setUint16(bpsOff, 8, true); dv.setUint16(bpsOff + 2, 8, true); dv.setUint16(bpsOff + 4, 8, true); dv.setUint16(bpsOff + 6, 8, true);
  u8.set(cmyk, dataOff);
  return Promise.resolve(new Blob([buf], { type: "image/tiff" }));
}

function convertCanvas() {
  const fmt = curFmt.value;
  const c = document.createElement("canvas");
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const ctx = c.getContext("2d");
  // 不透明落地（jpeg/cmyk/grayscale/rgb 不带 alpha）：先铺白底再画。
  if (fmt === "jpeg" || fmt === "cmyk" || fmt === "grayscale" || fmt === "rgb") {
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
  }
  ctx.drawImage(img, 0, 0);
  // #6 色彩转换（前端近似）
  if (fmt === "grayscale") {
    const id = ctx.getImageData(0, 0, c.width, c.height);
    const d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      const l = Math.round(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]);
      d[i] = d[i + 1] = d[i + 2] = l;
    }
    ctx.putImageData(id, 0, 0);
    return new Promise((res) => c.toBlob((b) => res(b), "image/png"));
  }
  if (fmt === "cmyk") return canvasToCmykTiff(c);
  if (fmt === "rgb") return new Promise((res) => c.toBlob((b) => res(b), "image/png")); // 展平为标准 RGB PNG
  if (fmt === "ico") return pngToIco(c);
  const q = fmt === "png" ? undefined : quality.value / 100;
  return new Promise((res) => c.toBlob((b) => res(b), "image/" + (ENC[fmt] || fmt), q));
}

function triggerDownload(blob, ext) {
  // 走 app 的 /download/ 页面（同源新标签页读 blob: URL），不再直接 <a download>。
  // 只保留最近一个 blob: URL：换新前先释放上一个（此前的 /download/ 标签页已同步读取过它）。
  if (lastDownloadUrl) { URL.revokeObjectURL(lastDownloadUrl); lastDownloadUrl = null; }
  const blobUrl = URL.createObjectURL(blob);
  lastDownloadUrl = blobUrl;
  const fname = baseName.value + "." + ext;
  window.open(
    "/download/?img_title=" +
      encodeURIComponent(fname) +
      "&img_path=" +
      encodeURIComponent(blobUrl) +
      "&prev_route1=/free-tools/image/converter&prev_route1_title=ImageConverter"
  );
}

// 设备 + 用户元数据（隐私：仅解析后的设备字段、匿名 visitorId、登录时的 userId；不含原始 UA，IP→国家由 Worker 加）。
function clientMeta() {
  if (!import.meta.client) return {};
  const ua = navigator.userAgent || "";
  const low = ua.toLowerCase();
  const tablet = /ipad|tablet/.test(low) || (/android/.test(low) && !/mobile/.test(low));
  const mobile = /iphone|ipod|windows phone/.test(low) || (/android/.test(low) && /mobile/.test(low));
  let tz = "";
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch (_) {}
  let visitorId = "";
  try {
    visitorId = localStorage.getItem("ft_vid") || "";
    if (!visitorId) {
      visitorId = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Date.now() + "-" + Math.random().toString(36).slice(2);
      localStorage.setItem("ft_vid", visitorId);
    }
  } catch (_) {}
  const u = (store && store.state && store.state.user) || {};
  const loggedIn = u.token ? 1 : 0;
  const userId = loggedIn && u.userInfo ? String(u.userInfo.user_info_id || "") : "";
  return {
    platform: tablet ? "tablet" : mobile ? "mobile" : "desktop",
    browser: /edg\//i.test(ua) ? "Edge" : /opr\/|opera/i.test(ua) ? "Opera" : /firefox|fxios/i.test(ua) ? "Firefox" : /chrome|crios/i.test(ua) ? "Chrome" : /safari/i.test(ua) ? "Safari" : "Other",
    os: /windows/i.test(ua) ? "Windows" : /iphone|ipad|ipod/i.test(ua) ? "iOS" : /mac os/i.test(ua) ? "macOS" : /android/i.test(ua) ? "Android" : /linux/i.test(ua) ? "Linux" : "Other",
    lang: navigator.language || "",
    tz,
    screenW: (window.screen && window.screen.width) || 0,
    screenH: (window.screen && window.screen.height) || 0,
    dpr: window.devicePixelRatio || 1,
    visitorId,
    loggedIn,
    userId,
  };
}

/* ---------- 转换观测埋点：sendBeacon 上报元数据（不含文件）到 /event（nuxt#17 / fastapi#13） ---------- */
function reportEvent(payload) {
  if (!import.meta.client) return;
  try {
    const eventId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : Date.now() + "-" + Math.random().toString(36).slice(2);
    const body = JSON.stringify({ eventId, ...clientMeta(), slug: slug.value, src: src.value, tgt: tgt.value, ...payload });
    if (navigator.sendBeacon) {
      navigator.sendBeacon("/api/free-tools/event", new Blob([body], { type: "application/json" }));
    } else {
      fetch("/api/free-tools/event", { method: "POST", body, headers: { "Content-Type": "application/json" }, keepalive: true }).catch(() => {});
    }
  } catch (_) {
    /* 埋点失败绝不影响转换 UX */
  }
}

/* ---------- Convert & Download ---------- */
async function onConvert() {
  if (!import.meta.client || stage.value !== "work" || busy.value) return;

  // 刺绣(pes/dst/exp)：先登录、再付费套餐拦截，未满足直接弹窗并 return（不触发转换/上传）。
  if (isEmbroidery.value) {
    if (!store.state.user.token) {
      const { openSignInModal } = useAuthModals();
      openSignInModal();
      return;
    }
    const pn = store.state.user.userInfo && store.state.user.userInfo.product_name;
    if (!pn || pn === "Free") {
      const { openPaywall } = usePaywallModal();
      openPaywall(["DST", "PES", "EXP", "JEF", "VP3", "HUS", "XXX", "EMD"]);
      return;
    }
  }

  errorMsg.value = "";
  const t0 = performance.now();

  // 后端格式（本切片：pes|dst）— 真正 POST 到同源代理
  if (isBackend.value) {
    if (!rawFile) return;
    busy.value = true;
    resultShown.value = false;
    resultUrl.value = ""; // 清掉上次可能的直连大产物 URL,避免复用旧结果
    // 长任务二级提示计时器:>=12s 仍在转换(同步阻塞 200 或异步 202 轮询都覆盖)→ 安抚文案。后端无进度,纯前端计时。
    let slowTimer = null;
    try {
      const target = curFmt.value;
      const qs =
        "to_format=" +
        encodeURIComponent(target) +
        "&from_format=" +
        encodeURIComponent(src.value) +
        "&embroidery_width_mm=" +
        encodeURIComponent(widthMm.value) +
        "&embroidery_color_count=" +
        encodeURIComponent(colorCount.value) +
        "&embroidery_row_spacing=" +
        encodeURIComponent(rowSpacing.value) +
        "&embroidery_stitch_length=" +
        encodeURIComponent(stitchLength.value) +
        "&embroidery_min_area=" +
        encodeURIComponent(minArea.value) +
        "&quality=" +
        encodeURIComponent(embQuality.value) +
        // 下载文件名(原名 + 目标后缀)→ worker 烤进结果对象 Content-Disposition,公开域名直发时自带原名。
        "&dl_name=" +
        encodeURIComponent((baseName.value || "sharkfoto-output") + "." + (target === "jpeg" ? "jpg" : target));
      // 媒体目标(视频/音频)：仅追加非缺省的可选参数，缺省 = 保持后端默认行为，不破坏现有转换。
      let mediaQs = "";
      if (isVideoTgt.value) {
        if (videoResolution.value) mediaQs += "&video_resolution=" + encodeURIComponent(videoResolution.value);
        const crf = VIDEO_CRF_MAP[videoQuality.value];
        if (crf != null) mediaQs += "&video_crf=" + encodeURIComponent(crf);
      } else if (isAudioTgt.value) {
        if (audioBitrate.value) mediaQs += "&audio_bitrate=" + encodeURIComponent(audioBitrate.value);
        if (audioSampleRate.value > 0) mediaQs += "&audio_sample_rate=" + encodeURIComponent(audioSampleRate.value);
      }
      slowTimer = setTimeout(() => { slowProcessingMsg.value = "Still processing — large files can take a minute…"; }, 12000);
      const res = await fetch("/api/free-tools/convert?" + qs + mediaQs, {
        method: "POST",
        headers: { "Content-Type": "application/octet-stream" },
        body: await rawFile.arrayBuffer(),
      });
      // 异步重负载(202 + jobId):刺绣/视频/音频/office → 轮询 /job 到 done,再取 /result。
      if (res.status === 202) {
        const { jobId } = await res.json();
        // 轮询到 done/error(退避 + 长上限,覆盖后端重转换真实耗时;见 useJobPoll)。onTick 升级"仍在处理"提示。
        const job = await pollJob(jobId, { onTick: (_j, ms) => { slowProcessingMsg.value = jobWaitMsg(ms); } });
        slowProcessingMsg.value = ""; // 循环结束(done/error/超时)清空提示
        if (!job || job.status !== "done") {
          errorMsg.value = (job && job.detail) || "Conversion timed out. Please try again or lower the quality/size.";
          reportEvent({ status: "error", mode: "backend", quality: embQuality.value, width: widthMm.value, colors: colorCount.value, inputBytes: rawFile ? rawFile.size : 0, durationMs: Math.round(performance.now() - t0), error: "async_" + ((job && job.status) || "timeout") });
          return;
        }
        const ext = (job.ext || target) === "jpeg" ? "jpg" : job.ext || target;
        const jobSize = Number(job.size) || 0;
        // 所有异步产物都已写 R2(带 Content-Disposition 元数据)→ 用 R2 公开域名直连、经 /download 页导航下载:
        // 不取 blob、不进 Nitro 代理、恒定内存、任意大小(dng/大视频也稳),与主站 AI 产物同一套下载路径。(nuxt#35)
        resultUrl.value = userR2Base + "/free-tools/results/" + encodeURIComponent(jobId);
        resultDlName.value = (baseName.value || "sharkfoto-output") + "." + ext;
        resultBlob.value = null;
        resultExt.value = ext;
        resultFmt.value = ext.toUpperCase();
        resultSize.value = fmtBytes(jobSize);
        resultShown.value = true;
        reloadGen.value++;
        reportEvent({ status: "ok", mode: "backend", quality: embQuality.value, width: widthMm.value, colors: colorCount.value, inputBytes: rawFile ? rawFile.size : 0, outputBytes: jobSize, stitchCount: Number(job.stitch) || 0, durationMs: Math.round(performance.now() - t0) });
        return;
      }
      if (!res.ok) {
        let detail = "";
        try {
          const j = await res.json();
          detail = j && j.detail ? j.detail : "";
        } catch (_) {
          /* not JSON */
        }
        errorMsg.value = detail || "Conversion failed (" + res.status + "). Please try again.";
        reportEvent({ status: "error", mode: "backend", quality: embQuality.value, width: widthMm.value, colors: colorCount.value, inputBytes: rawFile ? rawFile.size : 0, durationMs: Math.round(performance.now() - t0), error: "http_" + res.status });
        return;
      }
      const blob = await res.blob();
      const stitch = Number(res.headers.get("x-stitch-count")) || 0;
      const ext = target === "jpeg" ? "jpg" : target;
      resultBlob.value = blob;
      resultExt.value = ext;
      resultFmt.value = ext.toUpperCase();
      resultSize.value = fmtBytes(blob.size);
      resultShown.value = true;
      reloadGen.value++; // 转换完成：触发一次广告重载（与改参数区分开）
      reportEvent({ status: "ok", mode: "backend", quality: embQuality.value, width: widthMm.value, colors: colorCount.value, inputBytes: rawFile ? rawFile.size : 0, outputBytes: blob.size, stitchCount: stitch, durationMs: Math.round(performance.now() - t0) });
    } catch (err) {
      errorMsg.value = "Could not reach the conversion server. Please try again.";
      reportEvent({ status: "error", mode: "backend", quality: embQuality.value, width: widthMm.value, colors: colorCount.value, inputBytes: rawFile ? rawFile.size : 0, durationMs: Math.round(performance.now() - t0), error: "network" });
    } finally {
      if (slowTimer) clearTimeout(slowTimer);
      busy.value = false;
      slowProcessingMsg.value = ""; // 兜底清空,避免提示残留
    }
    return;
  }

  // 客户端 canvas 路径（png/jpg/webp/ico/gif）
  if (!img) {
    errorMsg.value = isBackend.value
      ? "This file needs server processing — please click Convert."
      : "This file can't be converted in your browser.";
    return;
  }
  busy.value = true;
  try {
    const blob = await convertCanvas();
    const f = curFmt.value;
    // #6：cmyk → CMYK TIFF(.tif)；grayscale/rgb → PNG。
    const ext = f === "jpeg" ? "jpg" : f === "cmyk" ? "tif" : f === "grayscale" || f === "rgb" ? "png" : f;
    resultBlob.value = blob;
    resultExt.value = ext;
    resultFmt.value = f === "cmyk" ? "CMYK (TIFF)" : f === "grayscale" ? "Grayscale (PNG)" : ext.toUpperCase();
    resultSize.value = fmtBytes(blob.size);
    resultShown.value = true;
    reloadGen.value++; // 转换完成：触发一次广告重载
    reportEvent({ status: "ok", mode: "client", quality: showQuality.value ? String(quality.value) : "", inputBytes: rawFile ? rawFile.size : 0, outputBytes: blob.size, durationMs: Math.round(performance.now() - t0) });
  } catch (err) {
    errorMsg.value = "Conversion failed. Please try a different file.";
    reportEvent({ status: "error", mode: "client", inputBytes: rawFile ? rawFile.size : 0, durationMs: Math.round(performance.now() - t0), error: "canvas_failed" });
  } finally {
    busy.value = false;
  }
}

/* ---------- 手动下载：转换完成后点 Download 才触发（走 /download/ 页） ---------- */
function onDownload() {
  if (!import.meta.client) return;
  // 异步产物:R2 公开 URL(对象自带 Content-Disposition 附件头)→ 经 /download 页导航下载(direct=1),
  // 不取 blob、不进 Nitro 代理、恒定内存、任意大小;/download 承载广告 + 产品推荐。(nuxt#35)
  if (resultUrl.value) {
    const qs = new URLSearchParams();
    qs.set("img_title", resultDlName.value || "sharkfoto-output." + (resultExt.value || "bin"));
    qs.set("img_path", resultUrl.value);
    if (resultSize.value) qs.set("file_size", resultSize.value);
    qs.set("direct", "1");
    qs.set("prev_route1", window.location.pathname);
    qs.set("prev_route1_title", (document.title || "Free Tools").split(/[—|]/)[0].trim());
    window.open("/download/?" + qs.toString(), "_blank");
    return;
  }
  // 客户端/同步产物(浏览器内 blob):triggerDownload 已走 /download 页(blob: URL)。
  if (!resultBlob.value) return;
  triggerDownload(resultBlob.value, resultExt.value || curFmt.value);
}
// 压缩器模式按钮文案用 Compress 系列；其余（converter / 配对）保持 Convert 系列。
const actionVerb = computed(() => (props.root === "compressor" ? "Compress" : "Convert"));
const actionVerbIng = computed(() => (props.root === "compressor" ? "Compressing" : "Converting"));
const resetLabel = computed(() => (props.root === "compressor" ? "Compress another file" : "Convert another file"));
// 主按钮：转换中→Processing/Converting；已转换→Download；否则→Convert。
const canDownload = computed(() => resultShown.value && (!!resultBlob.value || !!resultUrl.value));
// work 阶段：converter 选了能力表判定不可做的目标/源 → 拦截：主按钮禁用 + "Coming soon"。
const targetUnavailable = computed(() => isComingSoon.value);
const primaryLabel = computed(() => {
  if (targetUnavailable.value) return "Coming soon";
  if (busy.value) {
    if (isBackend.value) return slowProcessingMsg.value ? "Still processing" : "Processing";
    return actionVerbIng.value;
  }
  return canDownload.value ? "Download" : actionVerb.value;
});
function onPrimary() {
  if (busy.value || targetUnavailable.value) return;
  if (canDownload.value) onDownload();
  else onConvert();
}

/* ---------- reset（X / Convert another file） ---------- */
function reset() {
  revokeUrls();
  if (lastDownloadUrl) { URL.revokeObjectURL(lastDownloadUrl); lastDownloadUrl = null; }
  rawFile = null;
  img = null;
  stage.value = "drop";
  fileName.value = "";
  fileSize.value = "";
  resultShown.value = false;
  resultFmt.value = "";
  resultSize.value = "";
  resultBlob.value = null;
  resultUrl.value = "";
  resultExt.value = "";
  errorMsg.value = "";
  busy.value = false;
  if (fileInput.value) fileInput.value.value = "";
  // 根模式：清掉上次识别的源后缀 + 目标回到类目默认，避免「Convert another file」后 dropzone/目标锁死上次格式。
  if (props.root) {
    detectedSrc.value = "";
    pickedTgt.value = rootCat.value === "video" ? "mp4" : rootCat.value === "audio" ? "mp3" : "png";
  }
}

onMounted(() => {
  if (fileInput.value) fileInput.value.setAttribute("accept", sourceAccept.value);
  emit("stage-change", adReloadKey.value);
});
onBeforeUnmount(() => {
  revokeUrls();
  if (lastDownloadUrl) { URL.revokeObjectURL(lastDownloadUrl); lastDownloadUrl = null; }
});
</script>

<template>
  <!-- design 的转换器拖拽区/工作区 -->
  <div class="tool-stage">
    <!-- Coming soon:确认暂不可做的格式(无可靠工具/专有依赖/裸像素/非图像)→ 禁用 + 提示。
         仅 drop 阶段替代 dropzone;work 阶段(已上传后选了不支持目标)由主按钮就地拦截,不再整块替换。 -->
    <div v-if="isComingSoon && stage === 'drop'" class="dropzone coming-soon" aria-disabled="true">
      <div class="dz-ico">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 2" />
        </svg>
      </div>
      <h3>Coming soon</h3>
      <div class="hint">This conversion isn’t available yet — we’re working on it.</div>
      <button class="btn dz-btn cs-btn" type="button" disabled>Coming soon</button>
    </div>

    <!-- dropzone（SSR 静态渲染） -->
    <div
      v-show="stage === 'drop' && !batchMode && !isComingSoon"
      class="dropzone"
      :class="{ drag: dragging }"
      @click="onPickClick"
      @dragenter="onDragEnter"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <input ref="fileInput" type="file" :accept="sourceAccept" multiple hidden @change="onInputChange" />
      <div class="dz-ico">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 16V4M7 9l5-5 5 5" />
          <path d="M5 16v3a1 1 0 001 1h12a1 1 0 001-1v-3" />
        </svg>
      </div>
      <h3>{{ dropTitle }}</h3>
      <div class="hint">{{ hint }}</div>
      <div class="formats"><span>{{ sourceLabel }}</span></div>
      <div class="dz-or">Fast, private conversion — no sign-up, no watermark</div>
      <button class="btn btn-amber dz-btn" type="button" @click="onBrowse">{{ btnText }}</button>
    </div>

    <!-- batch 多文件队列（nuxt#21）：拖入 >1 个文件时进入；共享参数走 #params 插槽 -->
    <div v-if="batchMode" class="workspace show">
      <BatchQueue :batch="batch" :category="batchCategory" :client="batchClient" :file-count-max="20" @add-more="addMoreFiles">
        <template #params>
          <div v-if="root !== 'compressor' && !isEmbroidery" class="ctl">
            <label>Convert all to</label>
            <div v-if="root === 'converter'" class="seg">
              <select v-model="pickedTgt" class="root-select" aria-label="Target format">
                <option v-for="t in ROOT_TARGETS" :key="t" :value="t">{{ t.toUpperCase() }}</option>
              </select>
            </div>
            <div v-else class="seg"><button :data-fmt="curFmt" class="active" type="button">{{ targetLabel }}</button></div>
          </div>
          <div v-if="showQuality" class="ctl">
            <label>Quality <span style="color: #f4cd76">{{ quality }}%</span></label>
            <div class="rangewrap"><input type="range" min="10" max="100" v-model.number="quality" /></div>
          </div>
          <!-- 刺绣参数（批量,对齐 design headEmb:emb-bar 头 + emb-top(格式下拉 + 质量 seg) + emb-params 4列网格 + ep-cap;绑同一套 ref，buildBatchQuery 已在用） -->
          <template v-if="isEmbroidery">
            <div class="emb-bar">
              <span class="emb-h"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 2l9 5-9 5-9-5 9-5z" stroke-linejoin="round" /><path d="M3 12l9 5 9-5M3 17l9 5 9-5" stroke-linejoin="round" /></svg>Shared stitch settings</span>
              <span class="emb-pro"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>Pro tool</span>
            </div>
            <div class="emb-top">
              <label>Convert all to</label>
              <div v-if="root === 'converter'" class="emb-selwrap"><select v-model="pickedTgt" class="root-select" aria-label="Embroidery format"><option v-for="t in ROOT_TARGETS" :key="t" :value="t">{{ t.toUpperCase() }}</option></select></div>
              <div v-else class="seg"><button :data-fmt="curFmt" class="active" type="button">{{ targetLabel }}</button></div>
              <label>Quality</label>
              <div class="seg">
                <button v-for="q in EMB_QUALITIES" :key="q" type="button" :class="{ active: embQuality === q }" @click="embQuality = q">{{ q.charAt(0).toUpperCase() + q.slice(1) }}</button>
              </div>
            </div>
            <div class="emb-params">
              <label>Width (mm)</label><div class="rangewrap"><input type="range" min="20" max="300" step="5" v-model.number="widthMm" /><span class="rangeval">{{ widthMm }}</span></div>
              <label>Colors</label><div class="rangewrap"><input type="range" min="1" max="32" step="1" v-model.number="colorCount" /><span class="rangeval">{{ colorCount }}</span></div>
              <label>Row spacing (mm)</label><div class="rangewrap"><input type="range" min="0.3" max="2.5" step="0.05" v-model.number="rowSpacing" /><span class="rangeval">{{ rowSpacing.toFixed(2) }}</span></div>
              <label>Stitch length (mm)</label><div class="rangewrap"><input type="range" min="1.5" max="6" step="0.1" v-model.number="stitchLength" /><span class="rangeval">{{ stitchLength.toFixed(1) }}</span></div>
              <label>Min area (mm²)</label><div class="rangewrap"><input type="range" min="0.5" max="30" step="0.5" v-model.number="minArea" /><span class="rangeval">{{ minArea.toFixed(1) }}</span></div>
              <div class="ep-cap"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.5v.5" stroke-linecap="round" /></svg>One shared set of settings is applied to every file.</div>
            </div>
          </template>

          <!-- 视频/音频参数（批量） -->
          <template v-if="(isVideoTgt || isAudioTgt) && !isComingSoon">
            <div class="emb-params-grid">
              <template v-if="isVideoTgt">
                <div class="param-row"><label>Resolution</label><select v-model="videoResolution" class="root-select" aria-label="Video resolution"><option value="">Original</option><option value="1080">1080p</option><option value="720">720p</option><option value="480">480p</option><option value="360">360p</option></select></div>
                <div class="param-row"><label>Quality</label><select v-model="videoQuality" class="root-select" aria-label="Video quality"><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
              </template>
              <template v-else>
                <div class="param-row"><label>Bitrate</label><select v-model="audioBitrate" class="root-select" aria-label="Audio bitrate"><option value="">Original</option><option value="320k">320 kbps</option><option value="256k">256 kbps</option><option value="192k">192 kbps</option><option value="128k">128 kbps</option><option value="96k">96 kbps</option></select></div>
                <div class="param-row"><label>Sample rate</label><select v-model.number="audioSampleRate" class="root-select" aria-label="Audio sample rate"><option :value="0">Original</option><option :value="48000">48000 Hz</option><option :value="44100">44100 Hz</option><option :value="22050">22050 Hz</option></select></div>
              </template>
            </div>
          </template>

          <div v-if="!isEmbroidery" style="font-size: 12.5px; color: var(--tx-faint); font-weight: 600">One set of settings applies to all files — change the format / quality / settings once.</div>
        </template>
      </BatchQueue>
    </div>

    <!-- workspace（单文件，仅 client，载入文件后显示） -->
    <div v-if="stage === 'work' && !batchMode" class="workspace show">
      <div class="ws-grid">
        <div class="ws-preview">
          <video v-if="previewKind === 'video'" :src="previewUrl" controls playsinline preload="metadata" @loadedmetadata="onVideoMeta"></video>
          <div v-else-if="previewKind === 'audio'" class="media-prev">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M9 18V5l12-2v13" stroke-linecap="round" stroke-linejoin="round" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>
            <audio :src="previewUrl" controls></audio>
            <span class="mp-name">{{ fileName }}</span>
          </div>
          <div v-else-if="previewKind === 'file'" class="media-prev">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke-linejoin="round" /><path d="M14 2v6h6" stroke-linejoin="round" /></svg>
            <span class="mp-ext">{{ (detectedSrc || (fileName || "").split(".").pop() || "file").toUpperCase() }}</span>
            <span class="mp-name">{{ fileName }}</span>
          </div>
          <img v-else :src="previewUrl" :alt="'Preview of ' + (fileName || 'file')" />
        </div>
        <div class="ws-panel">
          <div class="ws-file">
            <img v-if="previewKind === 'image'" class="thumb" :src="thumbUrl" :alt="fileName" />
            <div v-else-if="previewKind === 'video'" class="thumb thumb-ph" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="2.5" y="5" width="19" height="14" rx="2" stroke-linejoin="round" /><path d="M10 9.2v5.6l5-2.8z" fill="currentColor" stroke="none" /></svg>
            </div>
            <div v-else-if="previewKind === 'audio'" class="thumb thumb-ph" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M9 18V5l12-2v13" stroke-linecap="round" stroke-linejoin="round" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>
            </div>
            <div v-else class="thumb thumb-ph" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke-linejoin="round" /><path d="M14 2v6h6" stroke-linejoin="round" /></svg>
            </div>
            <div class="meta">
              <div class="fn">{{ fileName }}</div>
              <div class="fsz">{{ fileSize }}</div>
            </div>
            <span class="rm" title="Remove" @click="reset">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg>
            </span>
          </div>

          <div v-if="root !== 'compressor'" class="ctl">
            <label>Convert to</label>
            <div v-if="root === 'converter'" class="seg">
              <select v-model="pickedTgt" class="root-select" aria-label="Target format">
                <option v-for="t in ROOT_TARGETS" :key="t" :value="t">{{ t.toUpperCase() }}</option>
              </select>
            </div>
            <div v-else class="seg">
              <button :data-fmt="curFmt" class="active" type="button">{{ targetLabel }}</button>
            </div>
          </div>

          <div v-if="showQuality" class="ctl">
            <label>Quality <span style="color: #f4cd76">{{ quality }}%</span></label>
            <div class="rangewrap">
              <input type="range" min="10" max="100" v-model.number="quality" />
            </div>
          </div>

          <!-- 刺绣转换参数（仅 pes/dst/exp 目标显示） -->
          <template v-if="isEmbroidery">
            <div class="ctl">
              <label>Quality</label>
              <div class="seg emb-seg">
                <button
                  v-for="q in EMB_QUALITIES"
                  :key="q"
                  type="button"
                  :class="{ active: embQuality === q }"
                  @click="embQuality = q"
                >
                  {{ q.charAt(0).toUpperCase() + q.slice(1) }}
                </button>
              </div>
            </div>

            <div class="emb-params-grid">
              <div class="param-row">
                <label>Width (mm)</label>
                <div class="rangewrap">
                  <input type="range" min="20" max="300" step="5" v-model.number="widthMm" />
                  <span class="rangeval">{{ widthMm }}</span>
                </div>
              </div>

              <div class="param-row">
                <label>Colors</label>
                <div class="rangewrap">
                  <input type="range" min="1" max="32" step="1" v-model.number="colorCount" />
                  <span class="rangeval">{{ colorCount }}</span>
                </div>
              </div>

              <div class="param-row">
                <label>Row spacing (mm)</label>
                <div class="rangewrap">
                  <input type="range" min="0.3" max="2.5" step="0.05" v-model.number="rowSpacing" />
                  <span class="rangeval">{{ rowSpacing.toFixed(2) }}</span>
                </div>
              </div>

              <div class="param-row">
                <label>Stitch length (mm)</label>
                <div class="rangewrap">
                  <input type="range" min="1.5" max="6" step="0.1" v-model.number="stitchLength" />
                  <span class="rangeval">{{ stitchLength.toFixed(1) }}</span>
                </div>
              </div>

              <div class="param-row">
                <label>Min area (mm²)</label>
                <div class="rangewrap">
                  <input type="range" min="0.5" max="30" step="0.5" v-model.number="minArea" />
                  <span class="rangeval">{{ minArea.toFixed(1) }}</span>
                </div>
              </div>
            </div>
          </template>

          <!-- 视频/音频质量控件（仅媒体目标且非 Coming soon；与刺绣不串台：媒体目标不会命中 isEmbroidery） -->
          <template v-if="(isVideoTgt || isAudioTgt) && !isComingSoon">
            <div class="emb-params-grid">
              <template v-if="isVideoTgt">
                <div class="param-row">
                  <label>Resolution</label>
                  <select v-model="videoResolution" class="root-select" aria-label="Video resolution">
                    <option v-for="o in resolutionOptions" :key="o.v" :value="o.v">{{ o.t }}</option>
                  </select>
                </div>
                <div class="param-row">
                  <label>Quality</label>
                  <select v-model="videoQuality" class="root-select" aria-label="Video quality">
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </template>
              <template v-else>
                <div class="param-row">
                  <label>Bitrate</label>
                  <select v-model="audioBitrate" class="root-select" aria-label="Audio bitrate">
                    <option value="320k">320 kbps</option>
                    <option value="256k">256 kbps</option>
                    <option value="192k">192 kbps</option>
                    <option value="128k">128 kbps</option>
                    <option value="96k">96 kbps</option>
                  </select>
                </div>
                <div class="param-row">
                  <label>Sample rate</label>
                  <select v-model.number="audioSampleRate" class="root-select" aria-label="Audio sample rate">
                    <option :value="0">Original</option>
                    <option :value="48000">48000 Hz</option>
                    <option :value="44100">44100 Hz</option>
                    <option :value="22050">22050 Hz</option>
                  </select>
                </div>
              </template>
            </div>
          </template>

          <!-- 长任务二级提示：后端处理 >=12s 时显示一行“仍在处理”，配旋转图标 -->
          <div v-if="busy && isBackend && slowProcessingMsg" class="note slow-msg" style="display: flex; align-items: center; gap: 9px; font-size: 12.5px; line-height: 1.5; margin-top: 2px; color: var(--tx-faint)">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex: none"><path d="M12 3a9 9 0 1 0 9 9" stroke-linecap="round" /></svg>
            {{ slowProcessingMsg }}
          </div>

          <div class="result-pill" :class="{ show: resultShown }">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" /></svg>
            <span>Converted to <span class="big">{{ resultFmt }}</span> · <span class="big">{{ resultSize }}</span></span>
          </div>

          <div v-if="errorMsg" class="note" style="display: flex; align-items: center; gap: 9px; font-size: 12.5px; color: #ff8585; line-height: 1.5; margin-top: 2px">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex: none"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.01" stroke-linecap="round" /></svg>
            {{ errorMsg }}
          </div>

          <!-- work 阶段选了不支持的目标：就地提示一行（按钮同时禁用并显示 Coming soon） -->
          <div v-if="targetUnavailable" class="note cs-note">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex: none"><circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 2" stroke-linecap="round" stroke-linejoin="round" /></svg>
            This target isn’t supported yet — pick another format.
          </div>

          <div class="ws-actions">
            <a
              class="btn btn-amber btn-lg"
              :class="{ 'is-disabled': targetUnavailable }"
              :aria-disabled="targetUnavailable || busy ? 'true' : 'false'"
              :style="targetUnavailable ? 'pointer-events:none;opacity:.55;cursor:not-allowed' : busy ? 'pointer-events:none;opacity:.7' : ''"
              @click="onPrimary"
            >
              <svg v-if="canDownload && !targetUnavailable" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align: -3px; margin-right: 7px"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke-linecap="round" stroke-linejoin="round" /></svg>{{ primaryLabel }}<span v-if="busy && !targetUnavailable" class="proc-dots" aria-hidden="true"><i></i><i></i><i></i></span>
            </a>
            <button class="btn btn-outline" type="button" @click="reset">{{ resetLabel }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 根转换器目标下拉(配对页 root="" 不出现) */
.root-select {
  width: 100%;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 10px 12px;
  color: var(--tx);
  font-family: var(--font-b);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  outline: none;
}
.root-select:focus { border-color: #f4cd76; }

/* Coming soon:暂不可做的格式 — 拖拽区样式但禁用观感 */
.coming-soon { cursor: default; opacity: 0.72; }
.coming-soon .dz-ico { opacity: 0.8; }
.cs-btn {
  background: var(--surface-2);
  border: 1px solid var(--line);
  color: var(--tx-2, #9aa);
  cursor: not-allowed;
  opacity: 0.85;
}
.cs-btn:hover { background: var(--surface-2); }

/* work 阶段「不支持目标」提示行（与 errorMsg .note 同风格，琥珀灰提示色） */
.cs-note {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 12.5px;
  color: var(--tx-dim, #f4cd76);
  line-height: 1.5;
  margin-top: 2px;
}

/* 刺绣 Quality 段按钮：共享 .seg button 用 flex:1 + flex-wrap:wrap + min-width:62px，
   四档(Draft/Standard/Fine/Ultra)标签宽窄不一时会换行、末行按钮被拉满 → 宽度不均。
   这里改用等分网格 + 不换行，保证四个按钮等宽整齐；并提高选中态对比。 */
/* 2×2 等分网格:窄面板(~250px)里 4 个一排会把 "Standard" 截成 "Stan…";2×2 每格够宽,标签完整显示。 */
/* 真 2×2:全局 .ft-scope .seg{display:flex} 会盖掉本组件的 display:grid(特异性相同、它后加载),
   所以这里必须 !important 强制网格,否则退化成一排四个 → "Standard" 文字溢出按钮。
   minmax(0,1fr) 让两格等分且能收缩,不撑破面板;不再加横向 padding,与上方下拉/参数左右对齐。 */
.emb-seg {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  gap: 8px;
}
.emb-seg button {
  flex: none !important;  /* 盖掉全局 .seg button{flex:1} */
  min-width: 0;
  width: 100%;
  padding: 9px 6px;
  text-align: center;
  white-space: nowrap;
}
.emb-seg button.active {
  background: color-mix(in oklab, var(--c-free) 26%, transparent);
  border-color: var(--c-free);
  color: #ffe7b0;
}

/* B/C：刺绣 5 个参数 2 列网格。minmax(0,1fr)+min-width:0 让滑块能收缩到面板内,
   否则 1fr(=minmax(auto,1fr)) 取滑块 min-content 宽 → 两列合计超出面板 → 右列(Colors/Stitch length)被截。 */
.emb-params-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 14px;
}
.param-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;          /* 允许收缩 */
}
.param-row .rangewrap {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;          /* 允许收缩 */
}
.param-row input[type="range"] {
  flex: 1;
  min-width: 0;          /* 关键:滑块可缩到 min-content 以下,不撑破列 */
  width: 100%;
}
.param-row .rangeval {
  font-weight: 700;
  color: #f4cd76;
  min-width: 38px;
  text-align: right;
  flex: none;
}
@media (max-width: 600px) {
  .emb-params-grid {
    grid-template-columns: 1fr;
  }
}

/* ===== 刺绣批量参数:对齐 design headEmb(emb-bar 头 / emb-top 格式+质量 / emb-params 4列网格 / ep-cap) ===== */
.emb-bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.emb-h { display: inline-flex; align-items: center; gap: 8px; font-family: var(--font-h); font-weight: 700; font-size: 13.5px; color: var(--tx); letter-spacing: 0.01em; }
.emb-h svg { width: 16px; height: 16px; color: #c9b6ff; }
.emb-pro { display: inline-flex; align-items: center; gap: 6px; font-family: var(--font-h); font-weight: 700; font-size: 11px; letter-spacing: 0.05em; text-transform: uppercase; color: #c9b6ff; background: color-mix(in oklab, var(--brand) 16%, transparent); border: 1px solid color-mix(in oklab, var(--brand) 40%, transparent); padding: 5px 10px; border-radius: 8px; white-space: nowrap; }
.emb-pro svg { width: 14px; height: 14px; }
.emb-top { display: grid; grid-template-columns: 124px 150px 84px minmax(0, 1fr); gap: 11px 22px; align-items: center; }
.emb-top > label { font-size: 12px; font-weight: 700; color: var(--tx-dim); letter-spacing: 0.01em; line-height: 1.25; }
.emb-top .seg { gap: 6px; flex-wrap: nowrap; }
.emb-top .seg button { min-width: 0; padding: 9px 12px; font-size: 13px; white-space: nowrap; flex: none; }
.emb-top .seg button.active { background: color-mix(in oklab, var(--c-free) 26%, transparent); border-color: var(--c-free); color: #ffe7b0; }
.emb-selwrap { position: relative; }
.emb-selwrap .root-select { -webkit-appearance: none; -moz-appearance: none; appearance: none; padding: 9px 38px 9px 13px; }
.emb-selwrap::after { content: ""; position: absolute; right: 15px; top: 50%; width: 8px; height: 8px; border-right: 2px solid var(--tx-faint); border-bottom: 2px solid var(--tx-faint); transform: translateY(-70%) rotate(45deg); pointer-events: none; }
.root-select:hover { border-color: var(--line-strong); }
.emb-params { display: grid; grid-template-columns: 124px minmax(0, 1fr) 124px minmax(0, 1fr); gap: 11px 22px; align-items: center; margin-top: 2px; padding-top: 12px; border-top: 1px solid var(--line); }
.emb-params > label { font-size: 12px; font-weight: 700; color: var(--tx-dim); letter-spacing: 0.01em; line-height: 1.25; }
.emb-params .rangewrap { display: flex; align-items: center; gap: 12px; min-width: 0; }
.emb-params .rangewrap input[type="range"] { flex: 1; min-width: 0; width: 100%; }
.emb-params .rangeval { font-weight: 700; color: #f4cd76; font-size: 14px; min-width: 42px; text-align: right; flex: none; }
.ep-cap { grid-column: 3 / -1; display: flex; align-items: center; gap: 8px; font-size: 11.5px; font-weight: 600; color: var(--tx-faint); line-height: 1.35; }
.ep-cap svg { width: 14px; height: 14px; color: var(--c-free); flex: none; }
@media (max-width: 600px) {
  .emb-top { grid-template-columns: 104px minmax(0, 1fr); }
  .emb-params { grid-template-columns: 104px minmax(0, 1fr); }
  .ep-cap { grid-column: 1 / -1; }
}

/* 长任务二级提示的旋转图标 */
@keyframes spin {
  to { transform: rotate(360deg); }
}
.slow-msg svg {
  animation: spin 1.5s linear infinite;
}

/* 主操作按钮 busy 时的动态省略号（3 个小圆点 opacity 错峰，跨浏览器稳，不用 content 动画） */
.proc-dots {
  display: inline-flex;
  gap: 3px;
  margin-left: 5px;
}
.proc-dots i {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.25;
  animation: procdot 1.2s infinite;
}
.proc-dots i:nth-child(2) {
  animation-delay: 0.2s;
}
.proc-dots i:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes procdot {
  0%, 80%, 100% { opacity: 0.25; }
  40% { opacity: 1; }
}

/* 工作区预览:video→<video>、audio→<audio>、占位(RAW/CAD/PDF 等无法浏览器解码的)— 非图片不再用 <img> 显示破图 */
.ws-preview video {
  max-height: 380px;
  max-width: 100%;
  width: auto;
  border-radius: 8px;
  background: #000;
  box-shadow: 0 18px 50px -18px rgba(0, 0, 0, 0.85);
}
.media-prev {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 20px;
  text-align: center;
}
.media-prev > svg {
  width: 60px;
  height: 60px;
  color: var(--c-free);
  opacity: 0.92;
}
.media-prev audio {
  width: min(300px, 82%);
}
.media-prev .mp-ext {
  font-weight: 800;
  font-size: 20px;
  letter-spacing: 0.04em;
  color: var(--tx);
}
.media-prev .mp-name {
  font-size: 12.5px;
  color: var(--tx-faint);
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* A：文件信息行缩略图占位（video/audio/file）——与 .ws-file .thumb 同尺寸(46×46/圆角11)，
   居中显示一个图标，避免对非图片文件用 <img> blob URL 显示破图。 */
.ws-file .thumb-ph {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-2);
  color: var(--c-free);
}
.ws-file .thumb-ph svg {
  width: 20px;
  height: 20px;
}
</style>
